import {
  DialogComponent,
  dialogIsModalTopLayer,
  findModalDialogAncestor
} from '@theme/dialog'
import { StandardEvents } from '@shopify/events'

/**
 * Sources whose cart line updates should NOT auto-open the drawer. Quick-order
 * tables reuse cart events for remove / quantity-change notifications,
 * so opening the drawer on those would surprise the merchant.
 *
 * @type {string[]}
 */
const AUTO_OPEN_BLOCKED_SOURCE_PREFIXES = ['quick-order-']

/**
 * Whether a successful add-to-cart should auto-open the drawer.
 *
 * @param {object | undefined} cart
 * @param {object | undefined} detail
 * @param {object} [options]
 * @param {boolean} [options.drawerEmpty] - SSR empty-state class on the dialog
 * @returns {boolean}
 */
export function shouldAutoOpenCartDrawer(
  cart,
  detail,
  { drawerEmpty = false } = {}
) {
  if (detail?.didError) return false

  if (typeof detail?.linesAdded === 'number' && detail.linesAdded > 0) {
    return true
  }

  if (typeof cart?.totalQuantity === 'number' && cart.totalQuantity > 0) {
    return true
  }

  return !drawerEmpty
}

/**
 * Schedules drawer open after cart line listeners and section morph complete.
 *
 * @param {() => void} openDrawer
 */
export function scheduleCartDrawerAutoOpen(openDrawer) {
  requestAnimationFrame(() => {
    requestAnimationFrame(openDrawer)
  })
}

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /**
   * Tracks the open lifecycle. We attach the installments-CTA listener and
   * any other per-open subscriptions to `signal`, then abort it on close so
   * listeners can't pile up across many open/close cycles.
   *
   * @type {AbortController | null}
   */
  #openController = null

  /**
   * Aborts the document-level cart update listener on disconnect.
   *
   * @type {AbortController | null}
   */
  #cartUpdateController = null

  /**
   * Whether the shopper intentionally opened the drawer for this cycle.
   * Section Rendering morph can strip `data-dialog-open` from the host;
   * this flag survives morph so we can restore top-layer state after updates.
   *
   * @type {boolean}
   */
  #drawerIntendedOpen = false

  /**
   * Trigger button that opens the drawer (for `aria-expanded` syncing). The
   * `ref="trigger"` slot in the snippet is the cart icon button.
   *
   * @returns {HTMLElement | null}
   */
  get #triggerButton() {
    const refTrigger = this.refs?.trigger
    if (refTrigger instanceof HTMLElement) return refTrigger

    return this.querySelector('[aria-controls="cart-drawer-dialog"]')
  }

  connectedCallback() {
    super.connectedCallback()
    if (this.dataset.dialogOpen === 'true') {
      this.#drawerIntendedOpen = true
    } else {
      const dialog = this.#getDialog()
      if (dialog instanceof HTMLDialogElement && dialog.open) {
        this.#drawerIntendedOpen = true
      }
    }

    queueMicrotask(() => this.#sanitizeClosedDrawerState())

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    )
    window.addEventListener('pageshow', this.#handlePageShow)

    this.#cartUpdateController = new AbortController()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    )
    window.removeEventListener('pageshow', this.#handlePageShow)

    this.#openController?.abort()
    this.#openController = null

    this.#cartUpdateController?.abort()
    this.#cartUpdateController = null
  }

  /**
   * @override
   */
  updatedCallback() {
    super.updatedCallback()
    this.#syncDrawerLifecycleAfterMorph()
  }

  /**
   * Whether the drawer should present after a successful add-to-cart.
   *
   * @param {object | undefined} cart
   * @param {object | undefined} detail
   * @returns {boolean}
   */
  #shouldAutoOpenAfterAdd(cart, detail) {
    const dialog = this.#getDialog()
    return shouldAutoOpenCartDrawer(cart, detail, {
      drawerEmpty: dialog?.classList.contains('cart-drawer--empty') ?? false
    })
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const maybeOpenDrawer =
      event.action === 'add' && this.hasAttribute('auto-open')

    if (maybeOpenDrawer) {
      event.promise
        ?.then(({ cart, detail }) => {
          if (detail?.didError) return

          const source = detail?.source
          if (
            typeof source === 'string' &&
            AUTO_OPEN_BLOCKED_SOURCE_PREFIXES.some((prefix) =>
              source.startsWith(prefix)
            )
          ) {
            return
          }

          const sourceModal = findModalDialogAncestor(event.target)

          const openDrawer = () => {
            if (!this.hasAttribute('auto-open')) return
            if (!this.#shouldAutoOpenAfterAdd(cart, detail)) return
            this.open()
          }

          const scheduleOpen = () => scheduleCartDrawerAutoOpen(openDrawer)

          if (sourceModal?.open) {
            sourceModal.addEventListener('close', scheduleOpen, { once: true })
            return
          }

          scheduleOpen()
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn('[cart-drawer] Event promise rejected:', error)
          }
        })
    }

    event.promise
      ?.then(({ detail }) => {
        const itemCount = detail?.itemCount
        if (itemCount === undefined) return

        const dialog = this.#getDialog()
        if (!dialog) return

        if (itemCount === 0) {
          dialog.classList.add('cart-drawer--empty')
          dialog.setAttribute('aria-labelledby', 'cart-drawer-heading-empty')
          dialog.classList.remove('cart-drawer__dialog--has-recommendations')
          dialog
            .querySelectorAll('.cart-drawer__summary, .cart-drawer__checkout')
            .forEach((node) => {
              node.remove()
            })
          dialog
            .querySelectorAll('shopify-accelerated-checkout-cart')
            .forEach((node) => {
              node.remove()
            })
        } else {
          dialog.classList.remove('cart-drawer--empty')
          dialog.setAttribute('aria-labelledby', 'cart-drawer-heading')
        }

        this.#syncDrawerLifecycleAfterMorph()
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-drawer] Event promise rejected:', error)
        }
      })
  }

  /**
   * Closes the drawer when the page is restored from the back/forward cache.
   * Without this, navigating away from the cart and pressing back can
   * resurrect a half-open drawer with stale state.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return
    if (!this.#drawerIntendedOpen) return
    this.closeDialog()
  }

  /**
   * Restores drawer lifecycle after Section Rendering morph. Morph can strip
   * `data-dialog-open` / `aria-expanded` while scroll lock and/or native
   * `dialog.open` drift, which collapses the drawer visually but leaves the
   * page unusable.
   */
  #syncDrawerLifecycleAfterMorph() {
    queueMicrotask(() => {
      if (this.isClosing) {
        return
      }

      const dialog = this.#getDialog()

      if (this.#drawerIntendedOpen) {
        if (!(dialog instanceof HTMLDialogElement)) return

        if (dialog.classList.contains('dialog-closing')) {
          dialog.classList.remove('dialog-closing')
        }

        if (!this.dataset.dialogOpen) {
          this.dataset.dialogOpen = 'true'
        }

        const trigger = this.#triggerButton
        if (trigger) trigger.setAttribute('aria-expanded', 'true')

        this.ensureModalTopLayer()
        this.ensureModalInteractionListeners()

        return
      }

      this.#sanitizeClosedDrawerState()
    })
  }

  /**
   * Clears stale layout / lifecycle flags when the drawer is not presented open.
   * Prevents `--has-recommendations` grid rules from applying to a closed dialog
   * and releases orphaned scroll lock from a lost native `open` state.
   */
  #sanitizeClosedDrawerState() {
    const dialog = this.#getDialog()
    if (!(dialog instanceof HTMLDialogElement)) return
    if (this.#drawerIntendedOpen || this.isClosing) return
    if (dialog.open && dialogIsModalTopLayer(dialog)) return

    if (dialog.open && !dialogIsModalTopLayer(dialog)) {
      try {
        if (typeof dialog.close === 'function') dialog.close()
      } catch {
        /* ignore */
      }

      dialog.removeAttribute('open')
    }

    dialog.classList.remove(
      'cart-drawer__dialog--has-recommendations',
      'dialog-closing'
    )

    delete this.dataset.dialogOpen

    const trigger = this.#triggerButton
    if (trigger) trigger.setAttribute('aria-expanded', 'false')

    this.releaseOrphanScrollLock()
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getDialog() {
    const refDialog = this.refs?.dialog
    if (refDialog instanceof HTMLElement && refDialog.tagName === 'DIALOG') {
      return refDialog
    }

    const byId = this.querySelector('#cart-drawer-dialog')
    if (byId instanceof HTMLElement && byId.tagName === 'DIALOG') {
      return byId
    }

    return null
  }

  /**
   * @override
   */
  onAfterOpen() {
    this.#drawerIntendedOpen = true
    const trigger = this.#triggerButton
    if (trigger) trigger.setAttribute('aria-expanded', 'true')
  }

  /**
   * @override
   */
  onBeforeClose() {
    // Stop cart-update resync from fighting an intentional close. Must run
    // before the close animation so morph during removal cannot reopen.
    this.#drawerIntendedOpen = false

    // Strip the recommendations layout class so a stale dialog doesn't
    // re-open with the wider grid before recommendations finish their
    // own hide() pass.
    const dialog = this.refs?.dialog
    dialog?.classList?.remove?.('cart-drawer__dialog--has-recommendations')

    // Cancel any pending recommendations work so a slow response can't
    // resolve into a hidden drawer.
    this.querySelectorAll('cart-recommendations-component').forEach((node) => {
      if (
        node instanceof HTMLElement &&
        typeof (/** @type {any} */ (node).abortPendingWork) === 'function'
      ) {
        try {
          /** @type {any} */ node.abortPendingWork()
        } catch {
          /* ignore */
        }
      }
    })

    this.#openController?.abort()
    this.#openController = null
  }

  /**
   * @override
   */
  onAfterClose() {
    this.#drawerIntendedOpen = false
    const trigger = this.#triggerButton
    if (trigger) trigger.setAttribute('aria-expanded', 'false')
  }

  open() {
    this.#drawerIntendedOpen = true
    this.showDialog()

    this.#openController?.abort()
    this.#openController = new AbortController()
    const { signal } = this.#openController

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        if (signal.aborted) return

        const installmentsContent = document.querySelector(
          'shopify-payment-terms'
        )?.shadowRoot
        const cta = installmentsContent?.querySelector(
          '#shopify-installments-cta'
        )
        cta?.addEventListener('click', this.closeDialog, { signal })
      })
      .catch(() => {
        /* whenDefined never rejects, but be defensive */
      })
  }

  close() {
    this.closeDialog()
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent)
}
